import type { PrijsfavorietenPart, ShareRow } from './report';

/** Sub-nav sections, in page order (ids double as scroll anchors). */
export const PF_SECTIONS = [
  { id: 'overzicht', label: 'Overzicht' },
  { id: 'engagement', label: 'Engagement' },
  { id: 'gameplay', label: 'Gameplay' },
  { id: 'gebruikersgedrag', label: 'Gebruikersgedrag' },
] as const;

/** Percentages Dutch-style: 39.5 → "39,5%". The design mixes "22.9%" and
 *  "39,5%"; nl-NL comma is correct for the audience, so we use it throughout. */
export function fmtPct(pct: number): string {
  return `${pct.toLocaleString('nl-NL', { maximumFractionDigits: 1 })}%`;
}

/** Games/player etc.: 7.49 → "7,49". */
export function fmtDecimal(n: number, digits = 2): string {
  return n.toLocaleString('nl-NL', { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

/*
 * ---- Deel 1 vs Deel 2 -------------------------------------------------------
 * Helpers for the two-part layout. Both parts arrive in the same payload shape,
 * with display strings already localised by the pipeline.
 */

type PartCampaign = { start: string; end: string; live?: boolean; currentDay: number; totalDays: number; calendarWeeks: string };

/**
 * The pipeline's duration strings back to seconds, so two parts can be compared:
 * "17m 4s" → 1024, "1u 03m" → 3780, "45s" → 45. Null when nothing parses.
 */
export function parseDurationSec(s: string): number | null {
  const part = (unit: string) => Number(new RegExp(`(\\d+)\\s*${unit}`).exec(s)?.[1] ?? 0);
  const sec = part('u') * 3600 + part('m') * 60 + part('s');
  return /\d/.test(s) ? sec : null;
}

/** Whole-percent change from `prev` to `cur`; null when there is nothing to compare against. */
export function pctChange(prev: number | null, cur: number | null): number | null {
  if (prev == null || cur == null || prev <= 0 || cur <= 0) return null;
  return Math.round(((cur - prev) / prev) * 100);
}

/** −30% / +4% / 0% — a true minus sign, as the design sets it. */
export function fmtChange(pct: number): string {
  if (pct === 0) return '0%';
  return `${pct > 0 ? '+' : '−'}${Math.abs(pct)}%`;
}

/**
 * "24 aug 2026" + "13 sep 2026" → "24 aug – 13 sep 2026" (or without the year).
 * Different years keep both, so a range across new year still reads right.
 */
export function fmtRange(start: string, end: string, { year = true }: { year?: boolean } = {}): string {
  const y = (s: string) => /\s(\d{4})$/.exec(s)?.[1];
  const bare = (s: string) => s.replace(/\s\d{4}$/, '');
  if (y(start) && y(start) !== y(end)) return year ? `${start} – ${end}` : `${bare(start)} – ${bare(end)}`;
  return year ? `${bare(start)} – ${end}` : `${bare(start)} – ${bare(end)}`;
}

/** "CW 35 / 36 / 37" → [35, 36, 37]. */
export function calendarWeekNumbers(calendarWeeks: string): number[] {
  return (calendarWeeks.match(/\d+/g) ?? []).map(Number);
}

/** "CW 35–37" — the footer's compact form. */
export function calendarWeekSpan(calendarWeeks: string): string {
  const cw = calendarWeekNumbers(calendarWeeks);
  if (cw.length === 0) return calendarWeeks;
  return cw.length === 1 ? `CW ${cw[0]}` : `CW ${cw[0]}–${cw[cw.length - 1]}`;
}

export type PartStatus = 'Lopend' | 'Afgerond' | 'Gepland';

/** Where a part stands: running, finished, or not started yet. */
export function partStatus(c: PartCampaign): PartStatus {
  if (c.live) return 'Lopend';
  return c.currentDay >= c.totalDays ? 'Afgerond' : 'Gepland';
}

/** One label, both parts' values — a row of paired bars. */
export type PairedRow = { label: string; previous: number; current: number };

/**
 * Pair two share lists by label, in Deel 1's order (the complete one), with any
 * label only Deel 2 has appended. A label a part lacks is 0% for that part —
 * e.g. early in Deel 2 nobody can have played "Twee weken" yet.
 */
export function pairShares(previous: ShareRow[], current: ShareRow[]): PairedRow[] {
  const labels = [...previous.map((r) => r.label), ...current.map((r) => r.label).filter((l) => !previous.some((r) => r.label === l))];
  const pct = (rows: ShareRow[], label: string) => rows.find((r) => r.label === label)?.pct ?? 0;
  return labels.map((label) => ({ label, previous: pct(previous, label), current: pct(current, label) }));
}

type Behaviour = PrijsfavorietenPart['behaviour'];

/** Pages of both parts, busiest in the running part first (Deel 1 breaks ties and orders pages Deel 2 has not seen yet). */
export function pairPages(previous: Behaviour, current: Behaviour): PairedRow[] {
  const names = [...new Set([...current.topPages.map((p) => p.name), ...previous.topPages.map((p) => p.name)])];
  const value = (b: Behaviour, name: string) => b.topPages.find((p) => p.name === name)?.value ?? 0;
  return names
    .map((label) => ({ label, previous: value(previous, label), current: value(current, label) }))
    .sort((a, b) => b.current - a.current || b.previous - a.previous);
}
